import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { NotifierService } from 'angular-notifier';
import { Observable } from 'rxjs';
import { share } from 'rxjs/operators';
import { App, ConfiguredApp, NewAppConfiguration } from '../../interfaces/app';

@Injectable({
  providedIn: 'root'
})
export class AppsService {
  public language = localStorage.getItem('lang') ?? this.translate.currentLang;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private notifier: NotifierService
  ) {}

  public getChainApps(chainId: number): Observable<App[]> {
    return this.http.get<App[]>(`/chains/${chainId}/apps`).pipe(
      share()
    );
  }

  public getAppById(chainId: number, appId: string): Observable<App> {
    return this.http.get<App>(`/chains/${chainId}/apps/${appId}`);
  }

  public getAllConfiguredApps(chainId: number): Observable<ConfiguredApp[]> {
    return  this.http.get<ConfiguredApp[]>(`/chains/${chainId}/apps/configured`).pipe(
      share()
    );
  }

  public getConfiguredAppById(chainId: number, appId: string): Observable<ConfiguredApp> {
    return this.http.get<ConfiguredApp>(`/chains/${chainId}/apps/configured/${appId}`);
  }

  public configureAppForChain(
    chainId: number,
    config: NewAppConfiguration
  ): Observable<ConfiguredApp> {
    const subject$ = this.http.post<ConfiguredApp>(
      `/chains/${chainId}/apps/configured`,
      { ...config }
    ).pipe(share());

    subject$.subscribe({
      next: () => {
        this.notifier.notify(
          'success',
          this.translate.instant('pages.apps.messages.addedAppSuccessfully')
        );
      }
    });

    return subject$;
  }

  public updateAppConfiguration(
    chainId: number,
    appId: string,
    config: NewAppConfiguration
  ): Observable<ConfiguredApp> {
    const subject$ = this.http.patch<ConfiguredApp>(
      `/chains/${chainId}/apps/configured/${appId}`,
      { ...config }
    ).pipe(share());

    subject$.subscribe({
      next: () => {
        this.notifier.notify(
          'success',
          this.translate.instant('pages.apps.messages.updateAppConfigSuccess')
        );
      }
    });

    return subject$;
  }

  public updateAppFiles(
    chainId: number,
    appId: string,
    files: string[]
  ): Observable<ConfiguredApp> {
    const subject$ = this.http.patch<ConfiguredApp>(
      `/chains/${chainId}/apps/configured/${appId}`,
      { files }
    ).pipe(share());

    subject$.subscribe({
      next: () => {
        this.notifier.notify(
          'success',
          this.translate.instant('pages.apps.messages.updateAppFilesSuccess')
        );
      }
    });

    return subject$;
  }

  public deleteAppConfiguration(
    chainId: number,
    appId: string
  ): Observable<void> {
    const subject$ = this.http.delete<void>(
      `/chains/${chainId}/apps/configured/${appId}`
    ).pipe(share());

    subject$.subscribe({
      next: () => {
        this.notifier.notify(
          'success',
          this.translate.instant('pages.apps.messages.deleteAppConfigSuccess')
        );
      }
    });

    return subject$;
  }

}
